import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import StockfishEngine from '../../../Components/StockfishEngine';
import {isEngineSupported} from '../../../Components/StockfishEngine/engine';

import {emptyEngineState, EngineState, EngineStatus, foldEngineResult, gameOverReason} from './engineLogic';

/**
 * How long to wait for a first word from the worker before calling it dead.
 * Longer than the engine's own asset probe (6s) plus handshake timeout (15s),
 * so a slow-but-healthy first load is never mistaken for a failure.
 */
export const STARTUP_TIMEOUT_MS = 25000;

export type {EngineStatus};

export type CourseEngineOptions = {
  /** The position to analyse — the board's CURRENT position. */
  fen: string;
  /** Mounts (and so spawns) the worker only while true. */
  enabled: boolean;
  /** MultiPV lines. */
  lines?: number;
  depth?: number;
  /** Hard cap per search, ms. Defaults to the engine's own 10s. */
  movetime?: number;
};

export type CourseEngine = {
  /** Render this anywhere in the tree (it draws nothing); null while off. */
  engineElement: React.ReactElement | null;
  state: EngineState;
  status: EngineStatus;
  thinking: boolean;
};

/**
 * Stockfish for a course board. See ./index.ts for the module's contract.
 */
export function useCourseEngine({fen, enabled, lines = 3, depth = 18, movetime}: CourseEngineOptions): CourseEngine {
  const supported = useMemo(() => isEngineSupported(), []);
  const ended = useMemo(() => gameOverReason(fen), [fen]);

  const [folded, setFolded] = useState<EngineState>(() => emptyEngineState(fen));
  const [doneFen, setDoneFen] = useState<string | null>(null);
  const [heard, setHeard] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  // Callbacks fire between renders; they must fold against the position the
  // board shows NOW, not the one they closed over.
  const fenRef = useRef(fen);
  fenRef.current = fen;

  // Switching off forgets the worker's history, so switching on is a fresh try.
  useEffect(() => {
    if (!enabled) {
      setHeard(false);
      setTimedOut(false);
      setDoneFen(null);
    }
  }, [enabled]);

  // StockfishEngine swallows init failures (it only logs), so silence is the
  // only signal that it never came up.
  useEffect(() => {
    if (!enabled || !supported || heard || ended) return undefined;
    const id = setTimeout(() => setTimedOut(true), STARTUP_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [enabled, supported, heard, ended]);

  const onResult = useCallback(
    (
      variationNumber: number,
      movesSAN: string[],
      baseTurn: 'w' | 'b',
      score: string,
      isMate: boolean,
      raw: string,
      searchedFen?: string,
    ) => {
      setHeard(true);
      setTimedOut(false);
      setFolded(s =>
        foldEngineResult(s, {variationNumber, movesSAN, baseTurn, score, isMate, raw, searchedFen}, fenRef.current, lines),
      );
    },
    [lines],
  );

  const onBestMove = useCallback((_uci: string, searchedFen: string) => {
    setHeard(true);
    setDoneFen(searchedFen);
  }, []);

  const state = useMemo(() => (folded.fen === fen ? folded : emptyEngineState(fen)), [folded, fen]);

  let status: EngineStatus;
  if (!enabled) status = 'off';
  else if (!supported || timedOut) status = 'unavailable';
  else if (ended) status = ended;
  else if (!heard) status = 'starting';
  else if (doneFen === fen) status = 'done';
  else status = 'thinking';

  const engineElement =
    enabled && supported ? (
      <StockfishEngine
        fen={fen}
        depth={depth}
        variations={lines}
        engineOn
        searchEnabled={!ended}
        movetime={movetime}
        onResult={onResult}
        onBestMove={onBestMove}
      />
    ) : null;

  return {engineElement, state, status, thinking: status === 'starting' || status === 'thinking'};
}
